import { getErrorMessage } from '@utils/error-message';
import { stubTrue } from 'lodash';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Typography from '@material-ui/core/Typography';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import {
  AbortableFetch,
  ActionButtonHandle,
  DetailContext,
  DetailMode,
  EvidenceContext,
  FileRef,
  PagedTableFieldContext,
  SnackbarContext,
  TableFieldToolbarButton,
  abortableFetch,
  useEventCallback,
} from '@eas/common-web';
import { OutgoingDocumentFields } from '@modules/document-outgoing/fields/general/general';
import { useValidationSchema } from '@modules/document-outgoing/outgoing-document-schema';
import { DECISION_SOURCE } from '@modules/es-file/es-file-hooked-sources';
import { AttachmentProvider } from '@composite/attachments/attachment-context';
import { DetailActionButton } from '@components/action-button/detail-action-button';
import { Document, ErrorObject, OutgoingDocument } from '@models';
import { AttachmentType, DecisionForm, EvidenceApiUrl } from '@enums';
import { AttachmentField } from '../../attachments/attachment-field';
import { DocumentDialogDataProvider } from '../document-context';

type DecisionCreateDto = {
  form: DecisionForm;
  decisionDate: string;
  attachments: {
    fullTextDocx: FileRef;
  };
};

type DecisionPrefill = Pick<
  DecisionCreateDto,
  'form' | 'decisionDate' | 'attachments'
>;

const toDecisionPrefill = (
  formData: OutgoingDocument
): DecisionPrefill | null => {
  if (!formData.decision) {
    return null;
  }

  const linkedDecision = formData.linkedDecision;

  if (
    !linkedDecision?.form ||
    !linkedDecision.decisionDate ||
    !linkedDecision.attachments?.fullTextDocx
  ) {
    return null;
  }

  return {
    form: linkedDecision.form,
    decisionDate: linkedDecision.decisionDate,
    attachments: {
      fullTextDocx: linkedDecision.attachments.fullTextDocx,
    },
  };
};

const mergeDecisionPrefill = (
  document: OutgoingDocument | null,
  decisionPrefill: DecisionPrefill | null
) => {
  if (!document || !decisionPrefill) {
    return document;
  }

  return {
    ...document,
    linkedDecision: {
      ...decisionPrefill,
      ...document.linkedDecision,
      attachments: {
        ...decisionPrefill.attachments,
        ...document.linkedDecision?.attachments,
      },
    },
  };
};

export function useAddOutgoingDocumentDialog(
  setResult: (document: OutgoingDocument | null) => void,
  setPersistedDecision: (value: boolean) => void,
  setDecisionPrefill: (value: DecisionPrefill | null) => void,
  persistedDecision: boolean,
  refreshSources: () => void
) {
  const fetch = useRef<AbortableFetch | null>(null);
  const { showSnackbar } = useContext(SnackbarContext);

  const decisionCreateDtoRef = useRef<DecisionCreateDto | null>(null);

  const syncDecisionCreateDto = useEventCallback(
    (
      formData: OutgoingDocument,
      { preserveExisting = false }: { preserveExisting?: boolean } = {}
    ) => {
      if (!formData.decision) {
        decisionCreateDtoRef.current = null;

        return;
      }

      const linkedDecision = formData.linkedDecision;

      if (
        !linkedDecision?.form ||
        !linkedDecision.decisionDate ||
        !linkedDecision.attachments?.fullTextDocx
      ) {
        if (!preserveExisting) {
          decisionCreateDtoRef.current = null;
        }

        return;
      }

      decisionCreateDtoRef.current = {
        form: linkedDecision.form,
        decisionDate: linkedDecision.decisionDate,
        attachments: {
          fullTextDocx: linkedDecision.attachments.fullTextDocx,
        },
      };
    }
  );

  const callCreateApi = useEventCallback(
    (id: string, formData: OutgoingDocument) => {
      setDecisionPrefill(toDecisionPrefill(formData));
      syncDecisionCreateDto(formData);

      return abortableFetch(`${EvidenceApiUrl.DOCUMENTS}`, {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify(formData),
      });
    }
  );

  const callEditApi = useEventCallback((formData: OutgoingDocument) => {
    // `linkedDecision: undefined` => USCR-2686
    const documentPayload = { ...formData, linkedDecision: undefined };

    return abortableFetch(`${EvidenceApiUrl.DOCUMENTS}/${formData.id}`, {
      method: 'PUT',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(documentPayload),
    });
  });

  const callCreateDecisionApi = useEventCallback((id: string) => {
    return abortableFetch(`${EvidenceApiUrl.DOCUMENTS}/${id}/decision`, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify(decisionCreateDtoRef.current),
    });
  });

  const handleEditDocument = useEventCallback(
    async (values: OutgoingDocument) => {
      try {
        syncDecisionCreateDto(values, {
          preserveExisting: persistedDecision,
        });

        if (fetch.current !== null) {
          fetch.current.abort();
        }

        fetch.current = callEditApi(values);
        await fetch.current.json();

        if (fetch.current !== null) {
          fetch.current.abort();
        }

        if (values.decision && decisionCreateDtoRef.current) {
          fetch.current = callCreateDecisionApi(values.id);
          await fetch.current.raw();
        }

        refreshSources();
        setResult(null);
        setPersistedDecision(false);
        setDecisionPrefill(null);
        decisionCreateDtoRef.current = null;
      } catch (e) {
        const err = e as ErrorObject<string>;

        const message = getErrorMessage(err);

        if (err.name !== 'AbortError') {
          showSnackbar(...message);
        }
      }
    }
  );

  return {
    callCreateApi,
    handleEditDocument,
  };
}

export function AddOutgoingDocumentButton({
  initialValues,
  disabled,
  refreshAfterConfirm = false,
}: {
  initialValues: Document;
  disabled: boolean;
  refreshAfterConfirm?: boolean;
}) {
  const intl = useIntl();

  const { hookedSources } = useContext(EvidenceContext);
  const { refreshAll } = useContext(DetailContext);
  const { source: documentSource } = useContext(PagedTableFieldContext);
  const decisionsSource = hookedSources[DECISION_SOURCE]?.source;

  const [result, setResult] = useState<OutgoingDocument | null>(null);
  const [persistedDecision, setPersistedDecision] = useState(false);
  const resultRef = useRef<OutgoingDocument | null>(result);
  const persistedDecisionRef = useRef(persistedDecision);
  resultRef.current = result;
  persistedDecisionRef.current = persistedDecision;
  const [decisionPrefill, setDecisionPrefill] =
    useState<DecisionPrefill | null>(null);
  const attachmentRef = useRef<ActionButtonHandle>(null);

  const attachmentInitialValues = mergeDecisionPrefill(result, decisionPrefill);

  const validationSchema = useValidationSchema();

  const refreshSources = () => {
    documentSource.reset();
    documentSource.loadMore();

    decisionsSource?.reset();
    decisionsSource?.loadMore();
  };

  const { callCreateApi, handleEditDocument } = useAddOutgoingDocumentDialog(
    setResult,
    setPersistedDecision,
    setDecisionPrefill,
    persistedDecision,
    () => {
      if (refreshAfterConfirm) {
        refreshAll();
      }
      refreshSources();
    }
  );

  const AttachmentFormFields = useCallback(() => {
    const document = resultRef.current;

    if (!document?.id) {
      return null;
    }

    return (
      <DocumentDialogDataProvider document={document}>
        {({ source }) => (
          <>
            <OutgoingDocumentFields
              decisionLocked={persistedDecisionRef.current}
              inPrompt
            />
            <AttachmentProvider>
              <AttachmentField
                label={
                  <FormattedMessage
                    id="ES__DOCUMENT_OUTGOING__DETAIL__PANEL_TITLE__INTERNAL"
                    defaultMessage="Přílohy odchozího dokumentu"
                  />
                }
                documentId={document.id}
                defaultName={document.summary ?? ''}
                attachments={source.data?.attachments ?? []}
                attachmentType={AttachmentType.OUTGOING}
                defaultExpanded={true}
                hasPermission={stubTrue}
              />
            </AttachmentProvider>
          </>
        )}
      </DocumentDialogDataProvider>
    );
  }, []);

  return (
    <>
      <DetailActionButton
        promptKey="ADD_OUTGOING_DOCUMENT"
        apiCall={callCreateApi}
        buttonLabel={intl.formatMessage({
          id: 'ES__DOCUMENTS_TOOLBAR__ADD_OUTGOING_DOCUMENT',
          defaultMessage: 'Přidat odchozí dokument',
        })}
        dialogTitle={intl.formatMessage({
          id: 'ES__DOCUMENTS_TOOLBAR__ADD_OUTGOING_DOCUMENT__TITLE',
          defaultMessage: 'Odchozí dokument',
        })}
        dialogText={intl.formatMessage({
          id: 'ES__DOCUMENTS_TOOLBAR__ADD_OUTGOING_DOCUMENT__TEXT',
          defaultMessage: 'Vyplňte formulář.',
        })}
        dialogWidth={1000}
        modes={[DetailMode.VIEW, DetailMode.EDIT]}
        ButtonComponent={function Button({ label, onClick }) {
          return (
            <TableFieldToolbarButton
              IconComponent={AddCircleOutlineIcon}
              show={true}
              disabled={disabled ?? false}
              title={label}
              onClick={onClick}
              color="primary"
              variant="contained"
            >
              <Typography variant="body2">
                {intl.formatMessage({
                  id: 'ES__DOCUMENTS_TOOLBAR__ADD_OUTGOING_DOCUMENT__BUTTON',
                  defaultMessage: 'Přidat odchozí',
                })}
              </Typography>
            </TableFieldToolbarButton>
          );
        }}
        FormFields={function Fields() {
          return (
            <DocumentDialogDataProvider document={result!}>
              {() => <OutgoingDocumentFields inPrompt />}
            </DocumentDialogDataProvider>
          );
        }}
        formInitialValues={initialValues}
        formValidationSchema={validationSchema}
        refreshAfterConfirm={false}
        onResult={async (result: OutgoingDocument) => {
          setResult(result);
          setPersistedDecision(Boolean(result.decision));

          requestAnimationFrame(() => {
            attachmentRef.current?.executeAction();
          });
        }}
      />
      <DetailActionButton
        ref={attachmentRef}
        promptKey="ADD_OUTGOING_DOCUMENT_ATTACHMENTS"
        closeCallback={refreshSources}
        buttonLabel={intl.formatMessage({
          id: 'ES__DOCUMENTS_TOOLBAR__ADD_OUTGOING_DOCUMENT_ATTACHMENTS',
          defaultMessage: 'Přidat přílohy',
        })}
        dialogTitle={intl.formatMessage({
          id: 'ES__DOCUMENTS_TOOLBAR__ADD_OUTGOING_DOCUMENT_ATTACHMENTS__TITLE',
          defaultMessage: 'Přílohy odchozího dokumentu',
        })}
        dialogText={intl.formatMessage({
          id: 'ES__DOCUMENTS_TOOLBAR__ADD_OUTGOING_DOCUMENT_ATTACHMENTS__TEXT',
          defaultMessage: 'Vyplňte formulář.',
        })}
        dialogWidth={800}
        dialogShowClose={false}
        modes={[]}
        submitCallback={handleEditDocument}
        refreshAfterConfirm={false}
        formInitialValues={attachmentInitialValues}
        dialogDisableBackdrop={true}
        FormFields={AttachmentFormFields}
      />
    </>
  );
}
